import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export interface DualMonthRangeValue {
  start: string
  end: string
}

const shortDate = (value: string) => value.replaceAll('-', '/')

export function isoDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function parseLocalDate(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function spanDays(start: string, end: string) {
  if (!start || !end) return 0
  return Math.round((parseLocalDate(end).getTime() - parseLocalDate(start).getTime()) / 86400000)
}

export function rangeDisplayLabel(range: DualMonthRangeValue) {
  if (!range.start || !range.end) return '未选择'
  return range.start === range.end ? shortDate(range.start) : `${shortDate(range.start)}-${shortDate(range.end)}`
}

function isoWeek(date: Date) {
  const value = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  value.setUTCDate(value.getUTCDate() + 4 - (value.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(value.getUTCFullYear(), 0, 1))
  return Math.ceil((((value.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}

function CalendarMonth({
  anchor,
  latestDate,
  earliestDate,
  draft,
  onPick,
  side,
  onMove,
}: {
  anchor: Date
  latestDate: string
  earliestDate?: string
  draft: DualMonthRangeValue
  onPick: (date: string) => void
  side: 'left' | 'right'
  onMove: (months: number) => void
}) {
  const year = anchor.getFullYear()
  const month = anchor.getMonth()
  const first = new Date(year, month, 1)
  const offset = (first.getDay() + 6) % 7
  const dayCount = new Date(year, month + 1, 0).getDate()
  const rowCount = Math.ceil((offset + dayCount) / 7)
  const rows = Array.from({ length: rowCount }, (_, row) => {
    const rowStart = new Date(year, month, 1 - offset + row * 7)
    return {
      week: isoWeek(rowStart),
      dates: Array.from({ length: 7 }, (_, column) => new Date(year, month, 1 - offset + row * 7 + column)),
    }
  })
  return (
    <section className="ad-calendar-month">
      <header className="ad-calendar-month-head">
        {side === 'left'
          ? (
            <div className="ad-calendar-nav">
              <button type="button" aria-label="上一年" onClick={() => onMove(-12)}><ChevronsLeft /></button>
              <button type="button" aria-label="上个月" onClick={() => onMove(-1)}><ChevronLeft /></button>
            </div>
          )
          : <span />}
        <strong>{year}年{String(month + 1).padStart(2, '0')}月</strong>
        {side === 'right'
          ? (
            <div className="ad-calendar-nav is-end">
              <button type="button" aria-label="下个月" onClick={() => onMove(1)}><ChevronRight /></button>
              <button type="button" aria-label="下一年" onClick={() => onMove(12)}><ChevronsRight /></button>
            </div>
          )
          : <span />}
      </header>
      <div className="ad-calendar-grid">
        <span />
        {['一', '二', '三', '四', '五', '六', '日'].map((day) => (
          <span className="ad-calendar-weekday" key={day}>{day}</span>
        ))}
        {rows.flatMap((row) => [
          <span className="ad-calendar-week" key={`w${year}-${month}-${row.week}`}>{row.week}</span>,
          ...row.dates.map((date) => {
            if (date.getMonth() !== month) return <span className="ad-calendar-empty" key={date.toISOString()} />
            const value = isoDate(date)
            const selected = value === draft.start || value === draft.end
            const inRange = Boolean(draft.start && draft.end && value >= draft.start && value <= draft.end)
            const disabled = value > latestDate || Boolean(earliestDate && value < earliestDate)
            return (
              <button
                type="button"
                key={value}
                disabled={disabled}
                onClick={() => onPick(value)}
                className={`ad-calendar-day${inRange ? ' is-range' : ''}${value === draft.start ? ' is-start' : ''}${value === draft.end ? ' is-end' : ''}${selected ? ' is-selected' : ''}`}
              >
                {date.getDate()}
              </button>
            )
          }),
        ])}
      </div>
    </section>
  )
}

export function DualMonthRangeCalendar({
  open,
  latestDate,
  earliestDate,
  value,
  maxSpanDays = 89,
  onClose,
  onConfirm,
}: {
  open: boolean
  latestDate: string
  earliestDate?: string
  value: DualMonthRangeValue
  maxSpanDays?: number
  onClose: () => void
  onConfirm: (range: DualMonthRangeValue) => void
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [draftRange, setDraftRange] = useState<DualMonthRangeValue>(value)
  const [draftStart, setDraftStart] = useState<string | null>(null)
  const [calendarAnchor, setCalendarAnchor] = useState(() => {
    const start = value.start || latestDate
    const first = parseLocalDate(start)
    first.setDate(1)
    return first
  })

  useEffect(() => {
    if (!open) return
    const draft = {
      start: value.start || latestDate,
      end: value.end || latestDate,
    }
    setDraftRange(draft)
    setDraftStart(null)
    const first = parseLocalDate(draft.start)
    first.setDate(1)
    if (draft.start.slice(0, 7) === draft.end.slice(0, 7)) first.setMonth(first.getMonth() - 1)
    setCalendarAnchor(first)
  }, [latestDate, open, value.end, value.start])

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) onClose()
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [onClose, open])

  if (!open || !latestDate) return null

  const rightMonth = new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + 1, 1)
  const overLimit = spanDays(draftRange.start, draftRange.end) > maxSpanDays
  const pickCalendarDay = (next: string) => {
    if (!draftStart) {
      setDraftStart(next)
      setDraftRange({ start: next, end: next })
      return
    }
    setDraftRange(next < draftStart ? { start: next, end: draftStart } : { start: draftStart, end: next })
    setDraftStart(null)
  }

  return (
    <div className="ad-custom-calendar" ref={rootRef}>
      <div className="ad-calendar-layout">
        <CalendarMonth
          anchor={calendarAnchor}
          latestDate={latestDate}
          earliestDate={earliestDate}
          draft={draftRange}
          onPick={pickCalendarDay}
          side="left"
          onMove={(months) => setCalendarAnchor(new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + months, 1))}
        />
        <CalendarMonth
          anchor={rightMonth}
          latestDate={latestDate}
          earliestDate={earliestDate}
          draft={draftRange}
          onPick={pickCalendarDay}
          side="right"
          onMove={(months) => setCalendarAnchor(new Date(calendarAnchor.getFullYear(), calendarAnchor.getMonth() + months, 1))}
        />
      </div>
      <footer>
        <span>
          {overLimit
            ? `明细最多查询${maxSpanDays + 1}天，请缩小区间`
            : `已选择 ${rangeDisplayLabel(draftRange)}`}
        </span>
        <div>
          <button type="button" onClick={onClose}>取消</button>
          <button
            type="button"
            className="is-confirm"
            disabled={!draftRange.start || !draftRange.end || overLimit}
            onClick={() => onConfirm(draftRange)}
          >
            确定
          </button>
        </div>
      </footer>
    </div>
  )
}
